import {
  Alert,
  Badge,
  Dropzone,
  FileList,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  UploadStatusBadge,
  VisuallyHidden,
} from '@hkdevs/design-system';
import { useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import {
  useCreateUpload,
  useUploadsById,
  type Upload,
  type UploadKind,
} from '../../api/queries/uploads';
import { useStatusAnnouncer } from './announcer';

/**
 * What the two upload drawers share (pc-dbb.10, pc-dbb.14): choosing files, sending them
 * one request per file (ADR 0020), and following each upload until it's processed.
 */

export const ACCEPT = '.csv,.xlsx,.xlsm';

export const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/** A column name. */
export const Col = ({ children }: { children: ReactNode }) => <Text as="code">{children}</Text>;

/** Adds files that aren't already chosen (by name and size). */
export function addFiles(current: readonly File[], added: readonly File[]) {
  const same = (a: File, b: File) => a.name === b.name && a.size === b.size;
  return [...current, ...added.filter((file) => !current.some((c) => same(c, file)))];
}

/** What became of one file's request: its upload, or why it wasn't accepted. */
export type Sent = { file: File; upload: Upload } | { file: File; error: string };

export const acceptedCount = (sent: readonly Sent[]) => sent.filter((s) => 'upload' in s).length;

/** The API's reason a file wasn't accepted, in the words it gave. */
function refusal(error: unknown) {
  if (error instanceof ApiError && error.status < 500) return error.message;
  return 'It couldn’t be uploaded. Check your connection and try again.';
}

/** Sends files one at a time, in order: each is its own upload, accepted or refused. */
export function useSendFiles() {
  const create = useCreateUpload();
  const [busy, setBusy] = useState(false);
  const send = async (files: readonly File[], kind: UploadKind, pendingPayDate?: string) => {
    setBusy(true);
    const results: Sent[] = [];
    for (const file of files) {
      try {
        const upload = await create.mutateAsync({
          file,
          kind,
          ...(pendingPayDate && { pendingPayDate }),
        });
        results.push({ file, upload });
      } catch (error) {
        results.push({ file, error: refusal(error) });
      }
    }
    setBusy(false);
    return results;
  };
  return { send, busy };
}

/** The Dropzone and the chosen files, with an explanation for an old .xls file. */
export function FilePicker({
  files,
  onFilesChange,
  disabled,
}: {
  files: readonly File[];
  onFilesChange: (files: File[]) => void;
  disabled: boolean;
}) {
  const [oldExcel, setOldExcel] = useState(false);
  return (
    <>
      <Dropzone
        accept={ACCEPT}
        multiple
        title="Drop files here"
        description="or browse · several files at once · .csv, .xlsx or .xlsm"
        disabled={disabled}
        onFiles={(added) => {
          setOldExcel(false);
          onFilesChange(addFiles(files, added));
        }}
        onReject={(rejected) =>
          setOldExcel(rejected.some((file) => file.name.toLowerCase().endsWith('.xls')))
        }
      />
      {oldExcel && (
        <Alert tone="danger" title="Old Excel files can’t be read">
          Save a .xls file as .xlsx or .csv, then add it again.
        </Alert>
      )}
      {files.length > 0 && (
        <Stack gap={2}>
          <Text tone="muted" size="meta">
            {plural(files.length, 'file selected', 'files selected')}
          </Text>
          <FileList
            files={files.map((file) => ({ name: file.name, size: file.size }))}
            {...(!disabled && {
              onRemove: (_, index: number) => onFilesChange(files.filter((__, i) => i !== index)),
            })}
          />
        </Stack>
      )}
    </>
  );
}

/**
 * Each sent file and its status, polled until it's completed or rejected; refused files
 * say why. The drawer is a dialog, so the table announces its own files' changes.
 */
export function SentFilesTable({
  sent,
  onOpenErrorReport,
}: {
  sent: readonly Sent[];
  onOpenErrorReport: (upload: { id: string; fileName: string }) => void;
}) {
  const accepted = sent.flatMap((s) => ('upload' in s ? [s.upload] : []));
  const polls = useUploadsById(accepted.map((upload) => upload.id));
  const current = accepted.map((upload, index) => polls[index]?.data ?? upload);
  const announcement = useStatusAnnouncer(polls.every((poll) => poll.data) ? current : undefined);
  const byId = new Map(current.map((upload) => [upload.id, upload]));
  return (
    <>
      <Table aria-label="Uploaded files">
        <TableHead>
          <TableCell>File</TableCell>
          <TableCell>Status</TableCell>
        </TableHead>
        <TableBody>
          {sent.map((s, index) =>
            'upload' in s ? (
              <SentRow
                key={s.upload.id}
                upload={byId.get(s.upload.id) ?? s.upload}
                onOpenErrorReport={onOpenErrorReport}
              />
            ) : (
              <TableRow key={`${s.file.name}-${index}`}>
                <TableCell rowHeader tone="mono">
                  <Stack as="span" gap={0}>
                    <span>{s.file.name}</span>
                    <Text tone="danger" size="meta">
                      {s.error}
                    </Text>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Badge tone="danger" dot>
                    Not uploaded
                  </Badge>
                </TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
      <VisuallyHidden role="status">{announcement}</VisuallyHidden>
    </>
  );
}

/** "96 records · new product type: Roofing" under a completed file. */
function completedNote(upload: Upload) {
  const added = upload.newProductTypes;
  const records = plural(upload.records ?? 0, 'record', 'records');
  if (added.length === 0) return records;
  return `${records} · new product ${added.length === 1 ? 'type' : 'types'}: ${added.join(', ')}`;
}

/** One sent file and its current status. */
function SentRow({
  upload,
  onOpenErrorReport,
}: {
  upload: Upload;
  onOpenErrorReport: (upload: { id: string; fileName: string }) => void;
}) {
  return (
    <TableRow>
      <TableCell rowHeader tone="mono">
        <Stack as="span" gap={0}>
          <span>{upload.fileName}</span>
          {upload.status === 'completed' && (
            <Text tone="muted" size="meta">
              {completedNote(upload)}
            </Text>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <UploadStatusBadge
          status={upload.status}
          {...(upload.status === 'rejected' && {
            onRejectedClick: () => onOpenErrorReport(upload),
            rejectedActionLabel: `Rejected, view the error report for ${upload.fileName}`,
          })}
        />
      </TableCell>
    </TableRow>
  );
}
